// LessonPlanFormPanel.jsx
import React, { memo } from "react";
import dayjs from "dayjs";
import { FaBookOpen, FaRedoAlt } from "react-icons/fa";
import {
  CustomButton,
  CustomDatePicker,
  CustomDropdown,
  CustomTextInput,
} from "../../../../components";
import ScheduleTable from "../../../../components/Scheduletable/Scheduletable";
import academicStyles from "../../../schoolCockpit/master/academicConfiguration/AcademicCreate.module.css";

const LessonPlanFormPanel = memo(function LessonPlanFormPanel({
  t,
  isEditMode,
  formLocked,
  formData,
  errors,
  bookOptions,
  topicOptions,
  subTopicOptions,
  sectionDropdownOptions,
  classDropdownValue,
  mediumDropdownValue,
  pageBounds,
  onBookChange,
  onSectionChange,
  onTopicChange,
  onSubTopicChange,
  onPageInputChange,
  onPageInputBlur,
  onViewBook,
  // Schedule
  scheduleDate,
  onScheduleDateChange,
  timetableLoading,
  timetableRows,
  timetablePeriods,
  scheduleLegendItems,
  selectedCellsForTable,
  onCellClick,
  // Generate
  onGenerate,
  generating,
  generateLabel,
  regenerateExhausted,
  academic_year,
  academic_end_date,
  academic_start_date,
}) {
  return (
    <div className={academicStyles.container}>
      <div className={academicStyles.header}>
        <div className={academicStyles.headerIcon}>
          <FaBookOpen aria-hidden />
        </div>
        <h1 className={academicStyles.headerTitle}>
          {isEditMode ? t.editLessonPlan : t.createLessonPlan}
        </h1>
        <div style={{ marginLeft: "auto" }}>
          <CustomButton
            type="button"
            variant="contained"
            className={academicStyles.cancelButton}
            fullWidth={false}
            onClick={onViewBook}
            disabled={!formData.book}
          >
            <FaBookOpen style={{ marginRight: 6 }} aria-hidden />
            {t.viewBook}
          </CustomButton>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={t.book}
            name="book"
            value={formData.book}
            onChange={onBookChange}
            options={bookOptions}
            placeholder={t.selectBook}
            required
            error={errors.book}
            disabled={formLocked}
          />
        </div>
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={t.class}
            name="class"
            value={classDropdownValue}
            onChange={() => {}}
            options={classDropdownValue ? [classDropdownValue] : []}
            placeholder={t.selectClass}
            disabled
          />
        </div>
        {/* <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={t.section}
            name="section"
            value={formData.sectionOption}
            onChange={onSectionChange}
            options={sectionDropdownOptions}
            placeholder={t.selectSection}
            disabled={!formData.book || formLocked}
          />
        </div> */}
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={t.medium}
            name="medium"
            value={mediumDropdownValue}
            onChange={() => {}}
            options={mediumDropdownValue ? [mediumDropdownValue] : []}
            placeholder={t.selectMedium}
            disabled
          />
        </div>

        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={t.topic}
            name="topic"
            value={formData.topic}
            onChange={onTopicChange}
            options={topicOptions}
            placeholder={t.selectTopicPlaceholder}
            required
            error={errors.topic}
            disabled={!formData.book || formLocked}
          />
        </div>
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={t.subTopic}
            name="subTopic"
            value={formData.subTopic}
            onChange={onSubTopicChange}
            options={subTopicOptions}
            placeholder={t.selectSubTopic}
            error={errors.subTopic}
            disabled={!formData.topic || formLocked}
          />
        </div>
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomTextInput
            label={t.startPageLabel}
            name="startPage"
            type="text"
            inputMode="numeric"
            value={formData.startPage}
            onChange={onPageInputChange}
            onBlur={onPageInputBlur}
            placeholder={
              pageBounds.min != null
                ? `${t.minPageShort} ${pageBounds.min}`
                : t.enterStartPage
            }
            error={errors.startPage}
            required
            disabled={formLocked}
          />
        </div>
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomTextInput
            label={t.endPageLabel}
            name="endPage"
            type="text"
            inputMode="numeric"
            value={formData.endPage}
            onChange={onPageInputChange}
            onBlur={onPageInputBlur}
            placeholder={
              pageBounds.max != null
                ? `${t.maxPageShort} ${pageBounds.max}`
                : t.enterEndPage
            }
            error={errors.endPage}
            required
            disabled={formLocked}
          />
        </div>
        {/* Completion Month */}
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomTextInput
            label={t.completionMonth}
            name="completionMonth"
            value={formData.month ? formData.month : "-"}
            placeholder={t.selectClass}
            disabled
            readOnly
          />
          {/* <CustomDatePicker
            label={t.completedDate}
            name="completedDate"
            value={formData.completedDate ? dayjs(formData.completedDate) : null}
            placeholder={t.completedDate}
            minDate={dayjs(new Date())}
            readOnly        
          /> */}
        </div>
      </div>

      {/* Schedule Date */}
      <div className="row mb-3">
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDatePicker
            label={t.scheduleDate}
            name="scheduleDate"
            value={scheduleDate ? dayjs(scheduleDate) : null}
            onChange={onScheduleDateChange}
            placeholder={t.selectDate}
            error={errors.scheduleDate}
            minDate={
              academic_start_date ? dayjs(academic_start_date) : undefined
            }
            maxDate={academic_end_date ? dayjs(academic_end_date) : undefined}
          />
        </div>
      </div>

      {/* Timetable */}
      <div className="row mt-2">
        <div className="col-12">
          {timetableLoading ? (
            <div style={{ padding: 24, textAlign: "center" }}>
              {t.loadingTimetable || "Loading timetable..."}
            </div>
          ) : timetableRows.length > 0 ? (
            <ScheduleTable
              periods={timetablePeriods}
              schedule={timetableRows}
              showLegend
              legendItems={scheduleLegendItems}
              selectable
              selectedCells={selectedCellsForTable}
              onCellClick={onCellClick}
            />
          ) : (
            <div style={{ padding: 24, textAlign: "center" }}>
              {t.noTimetableFound || "No timetable found for this week."}
            </div>
          )}
        </div>
      </div>

      {/* Generate / Re-Generate */}
      {!isEditMode && (
        <div
          className={academicStyles.actions}
          style={{ justifyContent: "center", marginTop: 24 }}
        >
          <CustomButton
            type="button"
            variant="contained"
            className={academicStyles.addButton}
            fullWidth={false}
            onClick={onGenerate}
            disabled={generating || regenerateExhausted}
          >
            <FaRedoAlt style={{ marginRight: 8 }} aria-hidden />
            {generateLabel}
          </CustomButton>
        </div>
      )}
    </div>
  );
});

export default LessonPlanFormPanel;
